import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { toast } from 'sonner';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { FiCheck, FiX, FiLoader, FiUser, FiMail, FiLock, FiArrowLeft, FiAlertCircle, FiUsers, FiShield, FiSettings } from 'react-icons/fi';
import { HiOutlineOfficeBuilding, HiOutlineGlobe, HiOutlineLightningBolt, HiOutlineUserGroup, HiOutlineExclamation } from 'react-icons/hi';
import api from '../../services/api';
import authService from '../../services/authService';
import EmailVerification from '../../components/auth/EmailVerification.jsx';

const OrganizationRegister = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [domainCheckLoading, setDomainCheckLoading] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  
  const [formData, setFormData] = useState({
    // Organization details
    organizationName: '',
    domain: '',
    description: '',
    
    // Admin user details
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    adminPasswordConfirm: '',
    adminJobTitle: ''
  });

  const { login } = useAuth();
  const navigate = useNavigate();

  // Check domain availability when domain changes
  useEffect(() => {
    const checkDomain = async () => {
      if (formData.domain && formData.domain.length > 3) {
        setDomainCheckLoading(true);
        try {
          const response = await api.get(`/organizations/domain-check/${formData.domain}`);
          if (response.data.success) {
            setDomainStatus(response.data.data);
          } else {
            setDomainStatus({ 
              available: false, 
              error: response.data.message,
              suggestions: response.data.suggestions 
            });
          }
        } catch (error) {
          console.error('Domain check failed:', error);
          const errorData = error.response?.data;
          setDomainStatus({ 
            available: false, 
            error: errorData?.message || 'Domain check failed',
            suggestions: errorData?.suggestions 
          });
        }
        setDomainCheckLoading(false);
      } else {
        setDomainStatus(null);
      }
    };

    const timeoutId = setTimeout(checkDomain, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.domain]);

  // Auto-fill admin email when domain changes
  useEffect(() => {
    if (formData.domain && formData.adminName && !formData.adminEmail.includes('@')) {
      const emailPrefix = formData.adminName.toLowerCase().replace(/\s+/g, '.');
      setFormData(prev => ({
        ...prev,
        adminEmail: `${emailPrefix}@${formData.domain}`
      }));
    }
  }, [formData.domain, formData.adminName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep1 = () => {
    if (!formData.organizationName.trim()) {
      toast.error('Organization name is required');
      return false;
    }
    if (!formData.domain.trim()) {
      toast.error('Domain is required');
      return false;
    }
    if (domainStatus && !domainStatus.available) {
      toast.error('Domain is not available or invalid');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.adminName.trim()) {
      toast.error('Admin name is required');
      return false;
    }
    if (!formData.adminEmail.trim()) {
      toast.error('Admin email is required');
      return false;
    }
    if (!formData.adminPassword) {
      toast.error('Password is required');
      return false;
    }
    if (formData.adminPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (formData.adminPassword !== formData.adminPasswordConfirm) {
      toast.error('Passwords do not match');
      return false;
    }

    // Validate admin email matches domain
    const emailDomain = formData.adminEmail.split('@')[1];
    if (emailDomain?.toLowerCase() !== formData.domain.toLowerCase()) {
      toast.error('Admin email must match organization domain');
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    try {
      setLoading(true);
      
      const registrationData = {
        organizationName: formData.organizationName,
        domain: formData.domain,
        description: formData.description,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword,
        adminJobTitle: formData.adminJobTitle
      };
      
      // Step 1: Send verification code
      await authService.sendOrgVerificationCode(registrationData);
      toast.success('Verification code sent to your email!');
      setVerificationStep(true);
      setVerificationError('');
    } catch (error) {
      const errorMessage = error.message || 'Registration failed';
      const suggestions = error.response?.data?.suggestions;
      
      toast.error(errorMessage);
      
      if (suggestions && suggestions.length > 0) {
        suggestions.forEach((suggestion, index) => {
          setTimeout(() => toast.info(suggestion), (index + 1) * 2000);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code submission
  const handleVerify = async (code) => {
    try {
      setVerificationLoading(true);
      setVerificationError('');
      
      const response = await authService.verifyAndRegisterOrg({ email: formData.adminEmail, code });
      
      if (response.success) {
        const { token, user, organization } = response.data;
        
        localStorage.setItem('token', token);
        
        toast.success(`Welcome to ${organization.name}! Your organization has been created successfully.`);
        navigate('/organization');
      }
    } catch (error) {
      setVerificationError(error.message || 'Verification failed');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle resend verification code
  const handleResendCode = async () => {
    await authService.resendVerificationCode(formData.adminEmail);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800 flex flex-col lg:flex-row">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-800 p-12">
        <div className="flex flex-col justify-between w-full max-w-lg mx-auto">
          {/* Header */}
          <div>
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur">
                <img src="/icon.png" alt="Tasklina" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-2xl font-bold text-white">Tasklina</span>
            </Link>
          </div>
          
          {/* Main Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur text-white text-sm font-medium mb-6">
                <HiOutlineOfficeBuilding className="w-4 h-4 mr-2" />
                For Company Administrators
              </div>
              <h1 className="text-4xl font-bold text-white leading-tight mb-4">
                Create Your Organization Workspace
              </h1>
              <p className="text-lg text-indigo-100 leading-relaxed">
                Set up a dedicated workspace for your company with centralized team management and enterprise features.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {[
                { icon: HiOutlineGlobe, text: 'Custom domain workspace' },
                { icon: HiOutlineUserGroup, text: 'Team member management' },
                { icon: FiShield, text: 'Role-based access control' },
                { icon: FiSettings, text: 'Organization-wide settings' }
              ].map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-medium">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <p className="text-2xl font-bold text-white">500+</p>
                <p className="text-indigo-200 text-sm">Organizations</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <p className="text-2xl font-bold text-white">50k+</p>
                <p className="text-indigo-200 text-sm">Team Members</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <p className="text-2xl font-bold text-white">99.9%</p>
                <p className="text-indigo-200 text-sm">Uptime</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-indigo-200 text-sm">
            <p>Just need a personal account?</p>
            <Link to="/auth/register" className="text-white font-semibold hover:underline">
              Create individual account
            </Link>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 lg:px-12">
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center space-x-3 justify-center mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-slate-700 flex items-center justify-center shadow-lg shadow-indigo-200 dark:shadow-indigo-900/50">
                <img src="/icon.png" alt="Tasklina" className="w-7 h-7 object-contain" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">Tasklina</span>
            </Link>
          </div>

          {/* Form Header with Back Button */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <Link 
                to="/auth/register-options" 
                className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors text-sm"
              >
                <FiArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Link>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Have an account?{' '}
                <Link to="/auth/login" className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-semibold">
                  Sign in
                </Link>
              </div>
            </div>
          </div>

          {verificationStep ? (
            /* Email Verification Step */
            <EmailVerification
              email={formData.adminEmail}
              onVerify={handleVerify}
              onResend={handleResendCode}
              onBack={() => setVerificationStep(false)}
              loading={verificationLoading}
              error={verificationError}
            />
          ) : (
          <>
          {/* Important Notice - Subtle info box */}
          <div className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <FiAlertCircle className="w-5 h-5 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  This creates a <strong>new organization workspace</strong>. If your company already uses Tasklina, 
                  ask your admin for an invitation instead.
                </p>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {step === 1 ? 'Organization Details' : 'Administrator Account'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {step === 1 
                ? 'Set up your company workspace' 
                : 'Create your admin credentials'}
            </p>
            
            {/* Progress Steps */}
            <div className="flex items-center justify-center mt-6 space-x-3">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
              }`}>
                1
              </div>
              <div className={`w-12 h-1 rounded ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'}`}></div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
              }`}>
                2
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
              {step === 1 ? (
                <>
                  {/* Organization Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Organization Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <HiOutlineOfficeBuilding className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="Acme Corporation"
                        required
                      />
                    </div>
                  </div>

                  {/* Domain */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Organization Domain
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <HiOutlineGlobe className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="domain"
                        value={formData.domain}
                        onChange={handleChange}
                        className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                          domainStatus?.available === false ? 'border-red-300 dark:border-red-500' : 
                          domainStatus?.available === true ? 'border-green-300 dark:border-green-500' : 'border-gray-200 dark:border-gray-600'
                        }`}
                        placeholder="acme.com"
                        required
                      />
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        {domainCheckLoading ? (
                          <FiLoader className="h-5 w-5 text-gray-400 animate-spin" />
                        ) : domainStatus?.available === true ? (
                          <FiCheck className="h-5 w-5 text-green-500" />
                        ) : domainStatus?.available === false ? (
                          <FiX className="h-5 w-5 text-red-500" />
                        ) : null}
                      </div>
                    </div>
                    
                    {/* Domain Status Message */}
                    {domainStatus && (
                      <div className={`mt-2 text-sm ${domainStatus.available ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {domainStatus.available ? (
                          <span className="flex items-center">
                            <FiCheck className="w-4 h-4 mr-1" />
                            Domain is available
                          </span>
                        ) : (
                          <span>{domainStatus.error || 'Domain is not available'}</span>
                        )}
                      </div>
                    )}

                    {/* Domain Suggestions */}
                    {domainStatus?.suggestions && domainStatus.suggestions.length > 0 && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Suggested alternatives:</p>
                        <div className="flex flex-wrap gap-2">
                          {domainStatus.suggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, domain: suggestion }))}
                              className="px-3 py-1 text-sm bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors text-gray-700 dark:text-gray-200"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description <span className="text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                      placeholder="Brief description of your organization"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!formData.organizationName || !formData.domain || (domainStatus && !domainStatus.available)}
                    className="w-full py-3.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Admin Setup
                  </button>
                </>
              ) : (
                <>
                  {/* Admin Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Your Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FiUser className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="adminName"
                        value={formData.adminName}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="John Smith"
                        required
                      />
                    </div>
                  </div>

                  {/* Admin Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Work Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FiMail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="adminEmail"
                        value={formData.adminEmail}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder={`you@${formData.domain || 'company.com'}`}
                        required
                      />
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      Must match your organization domain ({formData.domain})
                    </p>
                  </div>

                  {/* Job Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Job Title <span className="text-gray-400">(optional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FiUsers className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="adminJobTitle"
                        value={formData.adminJobTitle}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="CTO, IT Manager, etc."
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FiLock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="adminPassword"
                        value={formData.adminPassword}
                        onChange={handleChange}
                        className="w-full pl-12 pr-12 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FiLock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="adminPasswordConfirm"
                        value={formData.adminPasswordConfirm}
                        onChange={handleChange}
                        className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                          formData.adminPasswordConfirm && formData.adminPassword !== formData.adminPasswordConfirm
                            ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
                        }`}
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                        )}
                      </button>
                    </div>
                    {formData.adminPasswordConfirm && formData.adminPassword !== formData.adminPasswordConfirm && (
                      <p className="mt-1.5 text-xs text-red-500 dark:text-red-400">Passwords do not match</p>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 py-3.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <FiLoader className="w-5 h-5 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Organization'
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>

          {/* Footer Info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              As an organization admin, you'll be able to invite team members, 
              manage permissions, and configure workspace settings.
            </p>
          </div>

          {/* Terms */}
          <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
            By creating an organization, you agree to our{' '}
            <Link to="/legal/terms" className="text-indigo-600 hover:underline dark:text-indigo-400">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/legal/privacy" className="text-indigo-600 hover:underline dark:text-indigo-400">Privacy Policy</Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationRegister;
